import runtime from 'serviceworker-webpack-plugin/lib/runtime'
import * as Actions from 'state/actions'
import * as ActionTypes from 'state/types'
import { Session } from 'shared/utils/storage'

import iconNotification from 'assets/icons/notification.png'

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const vapidPublicKey = process.env.VAPID_PUBLIC_KEY
const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey)

export const notificationMiddleware = (() => store => next => action => {

  if (!('Notification' in window)) {
    // Notifications not supported
    console.error('Notifications: not supported')
    return next(action)
  }

  switch(action.type) {

    case ActionTypes.INIT_NOTIFICATIONS:{

      // console.info('Notifications: Starting notifications')

      Notification.requestPermission()

      // Check if push messaging is supported
      if (('serviceWorker' in navigator) && ('PushManager' in window)) {

        // console.info('Notifications: Register to runtime')

        runtime
          .register()
          .then(reg => {
            // console.info('Notifications: Done registering to runtime')

            const subscribe = () => {
              // console.info('Notifications: Registering a subscription')

              reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: convertedVapidKey
              })
                .then((subscription) => {
                  registerSubscription(store, subscription)

                  updateFlagNotificationSubscriptionRegistered()
                })
                .catch(err => {
                  if (Notification.permission === 'denied') {
                    // The user denied the notification permission which
                    // means we failed to subscribe and the user will need
                    // to manually change the notification permission to
                    // subscribe to push messages
                    console.error('Notifications: Permission for Notifications was denied', err)

                  } else {
                    // A problem occurred with the subscription, this can
                    // often be down to an issue or lack of the gcm_sender_id
                    // and / or gcm_user_visible_only
                    console.error('Notifications: Unable to subscribe to push.', err)
                  }
                })
            }

            const unsubscribe = (subscription, cb) => {
              // console.info('Notifications: Unsubscribing a subscription')

              subscription.unsubscribe().then(() => {
                // You've successfully unsubscribed
                // Running success callback
                cb()
              }).catch(err => {
                // Unsubscription failed
                console.error('Notifications: Unable to unsubscribe.', err)
              })
            }

            reg.pushManager
              .getSubscription()
              .then(subscription => {
                if(subscription && !isNotificationSubscriptionRegistered()) {
                  //Updating the subscription
                  unsubscribe(subscription, subscribe)

                  //Terminating execution
                  return
                }

                if (subscription) {
                  // console.info('Notifications: Subscription exists, subscribing')
                  registerSubscription(store, subscription)
                  return
                }

                subscribe()
              })
          })
          .catch(err => console.error('Notifications: failed to register runtime', err))
      }
      return next(action)
    }


    case ActionTypes.SHOW_NOTIFICATION: {

      Notification.requestPermission( (permission) => {
        if (permission !== 'granted') {
          console.error('Notifications: no permissions to show notification', permission)
          return
        }

        const {
          title,
          body,
          tag
        } = action

        const options = {
          title,
          body,
          tag,
          icon: iconNotification,
          iconUrl: `https://app.flow.ai${iconNotification}`
        }

        const notification = new Notification(title, options)
        notification.addEventListener('click', (e) => {
          window.focus()
          e.target.close()

          if(body === 'interaction') {
            window.open('/chat/unassigned')
          } else {
            window.open('/chat/unassigned')
          }
        }, false)
      })

      return next(action)
    }

    default:
      return next(action)
  }
})()

const registerSubscription = (store, subscription) => {
  // console.info('Notifications: Subscribing to a push client')

  // The subscription was successful
  const endpoint = subscription.endpoint
  const rawKey = subscription.getKey ? subscription.getKey('p256dh') : ''
  const key = rawKey ? btoa(String.fromCharCode.apply(null, new Uint8Array(rawKey))) : ''
  const rawAuthSecret = subscription.getKey ? subscription.getKey('auth') : ''
  const authSecret = rawAuthSecret ? btoa(String.fromCharCode.apply(null, new Uint8Array(rawAuthSecret))) : ''

  store.dispatch(Actions.subscribePushClient(endpoint, key, authSecret))
}

const NOTIFICATION_SUBSCRIPTION_REGISTERED_FLAG = 'notificationSubscriptionRegistered'

const isNotificationSubscriptionRegistered = () => {
  //@value(timestamp)
  const notificationSubscriptionRegistered = Session.getItem(NOTIFICATION_SUBSCRIPTION_REGISTERED_FLAG)
  const isRegistered = Boolean(notificationSubscriptionRegistered)

  return isRegistered
}

const updateFlagNotificationSubscriptionRegistered = () => {
  const timestamp = Date.now()

  Session.setItem(
    NOTIFICATION_SUBSCRIPTION_REGISTERED_FLAG,
    timestamp
  )
}
